import { MapPin, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { TherapistResult } from "@/hooks/use-chat";
import { useTherapistEnumLabel } from "@/lib/therapists/enum-label";

interface TherapistResultCardProps {
	therapist: TherapistResult;
}

export function TherapistResultCard({ therapist }: TherapistResultCardProps) {
	const enumLabel = useTherapistEnumLabel();
	const specializations = therapist.specializations || [];
	const city = therapist.activeAddress?.location?.city || "Unknown";
	const hasService = !!therapist.serviceName;
	const maxSpecs = hasService ? 2 : 3;

	return (
		<Card className="overflow-hidden border-l-4 border-l-primary/60">
			<CardContent className="p-3">
				<div className="flex flex-col gap-1.5">
					<div className="flex items-center justify-between">
						<h4 className="text-sm font-semibold">{therapist.name}</h4>
						<Badge variant="secondary" className="text-xs">
							{therapist.gender === "MALE" ? "Male" : "Female"}
						</Badge>
					</div>

					<div className="flex items-center gap-1 text-xs text-muted-foreground">
						<MapPin className="size-3" />
						<span>{city}</span>
						{therapist.distanceKm != null && (
							<span className="ml-1">({therapist.distanceKm} km)</span>
						)}
					</div>

					{(hasService || specializations.length > 0) && (
						<div className="flex flex-wrap gap-1 mt-0.5">
							{hasService && (
								<Badge variant="default" className="text-xs px-1.5 py-0">
									{therapist.serviceName?.replace(/_/g, " ")}
								</Badge>
							)}
							{specializations.slice(0, maxSpecs).map((spec) => (
								<Badge
									key={spec}
									variant="outline"
									className="text-xs px-1.5 py-0"
								>
									{enumLabel("specialization", spec)}
								</Badge>
							))}
							{specializations.length > maxSpecs && (
								<span className="text-xs text-muted-foreground">
									+{specializations.length - maxSpecs}
								</span>
							)}
						</div>
					)}

					<div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
						<span className="flex items-center gap-1">
							<Star className="size-3 fill-amber-400 text-amber-400" />
							{therapist.employmentType === "KARPIS" ? "Internal" : "Mitra"}
						</span>
						<span>{therapist.registrationNumber}</span>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
